import React from 'react';
import PropTypes from 'prop-types';
import { 
  Button, Typography, Box, List, ListItem, ListItemText, 
  ListItemSecondaryAction, IconButton, Tooltip, Grid,
  makeStyles, Paper, Divider,
  ButtonGroup,
  TextField
} from '@material-ui/core';
import Icon from '@mdi/react';
import {
  mdiDelete,
  mdiMapMarker,
  mdiCircleOutline,
  mdiVectorPolygon,
  mdiVectorLine,
  mdiCursorDefault
} from '@mdi/js';

const useStyles = makeStyles((theme) => ({
  iconButton: {
    marginRight: theme.spacing(1),
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    },
    '&.selected': {
      backgroundColor: theme.palette.action.selected
    }
  },
  colorSwatch: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: theme.spacing(1)
  },
  toolbarButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1)
  }
}));

// Drawing Toolbar Component
export const DrawingToolbar = ({ 
  onToolActivate, 
  isDrawingMode, 
  activeAssetType,
  activeCategory
}) => {
  const classes = useStyles();
  
  const handleToolActivation = (toolType) => {
    // Add validation before calling the parent handler
    if (!activeAssetType || !activeCategory) {
      alert("Please select an asset type and category before drawing");
      return;
    }
    
    onToolActivate(toolType);
  };

  return (
    <Paper elevation={1} style={{ padding: 8 }}>
      <Typography variant="subtitle2" gutterBottom>Drawing Tools</Typography>
      <Box display="flex" flexWrap="wrap">
        {activeAssetType === 'zone' && (
          <Tooltip title="Draw Zone (Polygon)">
            <Button 
              className={classes.toolbarButton}
              variant="outlined" 
              size="small"
              onClick={() => handleToolActivation('polygon')}
              disabled={isDrawingMode || !activeCategory}
              startIcon={<Icon path={mdiVectorPolygon} size={0.8} />}
            >
              Zone
            </Button>
          </Tooltip>
        )}
        
        {activeAssetType === 'pipe' && (
          <Tooltip title="Draw Pipe (Line)">
            <Button 
              className={classes.toolbarButton}
              variant="outlined" 
              size="small"
              onClick={() => handleToolActivation('polyline')}
              disabled={isDrawingMode || !activeCategory}
              startIcon={<Icon path={mdiVectorLine} size={0.8} />}
            >
              Pipe
            </Button>
          </Tooltip>
        )}
        
        {(activeAssetType === 'sprinkler' ||
          activeAssetType === 'valve' ||
          activeAssetType === 'controller' ||
          activeAssetType === 'flush_cap' ||
          activeAssetType === 'misc') && (
          <Tooltip title="Place Icon (Marker)">
            <Button 
              className={classes.toolbarButton}
              variant="outlined" 
              size="small"
              onClick={() => handleToolActivation('marker')}
              disabled={isDrawingMode || !activeCategory}
              startIcon={<Icon path={mdiMapMarker} size={0.8} />}
            >
              Place Icon
            </Button>
          </Tooltip>
        )}
        
        {activeAssetType === 'sprinkler' && (
          <Tooltip title="Draw Coverage (Circle)">
            <Button 
              className={classes.toolbarButton}
              variant="outlined" 
              size="small"
              onClick={() => handleToolActivation('circle')}
              disabled={isDrawingMode || !activeCategory}
              startIcon={<Icon path={mdiCircleOutline} size={0.8} />}
            >
              Coverage
            </Button>
          </Tooltip>
        )}
        
        {isDrawingMode && (
          <Tooltip title="Cancel Drawing">
            <Button 
              className={classes.toolbarButton}
              variant="outlined" 
              size="small"
              color="secondary"
              onClick={() => {
                // Add cancel drawing logic here
                if (mapInstanceRef.current) {
                  // This would be inside the main component
                  // mapInstanceRef.current._cancelDrawing();
                  setIsDrawingActive(false);
                }
              }}
              startIcon={<Icon path={mdiCursorDefault} size={0.8} />}
            >
              Cancel
            </Button>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
};


DrawingToolbar.propTypes = {
  onToolActivate: PropTypes.func.isRequired,
  isDrawingMode: PropTypes.bool.isRequired,
  activeAssetType: PropTypes.string,
  activeCategory: PropTypes.string
};

// Asset List Component
export const AssetList = ({ 
  assets, 
  selectedAssetId, 
  onSelectAsset, 
  onDeleteAsset, 
  onClearAll, 
  isDrawingMode
}) => {
  const classes = useStyles();

  // Group assets by type
  const assetsByType = assets.reduce((acc, asset) => {
    if (!acc[asset.assetType]) acc[asset.assetType] = [];
    acc[asset.assetType].push(asset);
    return acc;
  }, {});

  if (assets.length === 0) {
    return (
      <Paper elevation={1} style={{ padding: 16 }}>
        <Typography variant="body2" align="center" color="textSecondary">
          No assets added yet. Use the drawing tools to add irrigation components.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={1}>
      <List dense disablePadding>
        {Object.entries(assetsByType).map(([type, typeAssets]) => (
          <React.Fragment key={type}>
            <ListItem>
              <ListItemText 
                primary={type.replace('_', ' ').toUpperCase()} 
                primaryTypographyProps={{ variant: 'subtitle2' }}
              />
              <Typography variant="caption" color="textSecondary">
                {typeAssets.length} {typeAssets.length === 1 ? 'item' : 'items'}
              </Typography>
            </ListItem>
            <Divider />
            {typeAssets.map(asset => (
              <ListItem 
                key={asset.id} 
                button
                className={`${classes.listItem} ${selectedAssetId === asset.id ? 'selected' : ''}`}
                onClick={() => onSelectAsset(asset)}
                disabled={isDrawingMode}
              >
                <ListItemText
                  primary={asset.name}
                  secondary={asset.categoryId || asset.assetType}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    size="small" 
                    onClick={() => onDeleteAsset(asset.id)}
                    disabled={isDrawingMode}
                  >
                    <Icon path={mdiDelete} size={0.8} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            <Divider />
          </React.Fragment>
        ))}
      </List>
      <Box p={1} display="flex" justifyContent="flex-end">
        <Button
          size="small"
          color="secondary"
          onClick={onClearAll}
          disabled={isDrawingMode}
        >
          Clear All
        </Button>
      </Box>
    </Paper>
  );
};

AssetList.propTypes = {
  assets: PropTypes.array.isRequired,
  selectedAssetId: PropTypes.string,
  onSelectAsset: PropTypes.func.isRequired,
  onDeleteAsset: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  isDrawingMode: PropTypes.bool.isRequired
};

// Asset Editor Component
export const AssetEditor = ({
  editingAsset,
  onEditChange,
  onStyleChange,
  onSave,
  onCancel,
  isDrawingMode
}) => {
  const classes = useStyles();

  if (!editingAsset) {
    return (
      <Paper elevation={1} style={{ padding: 16 }}>
        <Typography variant="body2" align="center" color="textSecondary">
          Select an asset to edit its properties
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} style={{ padding: 16 }}>
      <Typography variant="subtitle2" gutterBottom>Edit Asset Properties</Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Name"
            name="name"
            value={editingAsset.name || ''}
            onChange={onEditChange}
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            disabled={isDrawingMode}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            label="Notes"
            name="notes"
            value={editingAsset.notes || ''}
            onChange={onEditChange}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            size="small"
            margin="dense"
            disabled={isDrawingMode}
          />
        </Grid>
        
        {/* Show different style options based on asset type */}
        {editingAsset.assetType === 'zone' && (
          <>
            <Grid item xs={6}>
              <TextField
                label="Border Color"
                name="color"
                type="color"
                value={editingAsset.style?.color || '#000000'}
                onChange={(e) => onStyleChange('color', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                disabled={isDrawingMode}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Fill Color"
                name="fillColor"
                type="color"
                value={editingAsset.style?.fillColor || '#000000'}
                onChange={(e) => onStyleChange('fillColor', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                disabled={isDrawingMode}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Border Width"
                name="weight"
                type="number"
                InputProps={{ inputProps: { min: 1, max: 10, step: 1 } }}
                value={editingAsset.style?.weight || 2}
                onChange={(e) => onStyleChange('weight', parseInt(e.target.value))}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                disabled={isDrawingMode}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Fill Opacity"
                name="fillOpacity"
                type="number"
                InputProps={{ inputProps: { min: 0, max: 1, step: 0.1 } }}
                value={editingAsset.style?.fillOpacity || 0.3}
                onChange={(e) => onStyleChange('fillOpacity', parseFloat(e.target.value))}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                disabled={isDrawingMode}
              />
            </Grid>
          </>
        )}
        
        {editingAsset.assetType === 'pipe' && (
          <>
            <Grid item xs={6}>
              <TextField
                label="Line Color"
                name="color"
                type="color"
                value={editingAsset.style?.color || '#000000'}
                onChange={(e) => onStyleChange('color', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                disabled={isDrawingMode}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Line Width"
                name="weight"
                type="number"
                InputProps={{ inputProps: { min: 1, max: 10, step: 1 } }}
                value={editingAsset.style?.weight || 3}
                onChange={(e) => onStyleChange('weight', parseInt(e.target.value))}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                disabled={isDrawingMode}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Opacity"
                name="opacity"
                type="number"
                InputProps={{ inputProps: { min: 0, max: 1, step: 0.1 } }}
                value={editingAsset.style?.opacity || 1}
                onChange={(e) => onStyleChange('opacity', parseFloat(e.target.value))}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                disabled={isDrawingMode}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Line Style"
                name="dashArray"
                select
                value={editingAsset.style?.dashArray || ''}
                onChange={(e) => onStyleChange('dashArray', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                disabled={isDrawingMode}
                SelectProps={{ native: true }}
              >
                <option value="">Solid</option>
                <option value="5,5">Dashed</option>
                <option value="1,5">Dotted</option>
                <option value="10,5,2,5">Dash-Dot</option>
              </TextField>
            </Grid>
          </>
        )}
        
        {editingAsset.assetType === 'sprinkler' && editingAsset.geometry?.type === 'Circle' && (
          <Grid item xs={12}>
            <TextField
              label="Coverage Radius (ft)"
              name="radius"
              type="number"
              InputProps={{ inputProps: { min: 1, step: 0.5 } }}
              // Convert from meters to feet for display
              value={(editingAsset.geometry.radius * 3.28084).toFixed(1)}
              onChange={(e) => {
                // Convert back to meters for storage
                const radiusInMeters = parseFloat(e.target.value) / 3.28084;
                const updatedGeometry = {
                  ...editingAsset.geometry,
                  radius: radiusInMeters
                };
                // Need a custom handler for this case
                onEditChange({ target: { name: 'geometry', value: updatedGeometry } });
              }}
              fullWidth
              variant="outlined"
              size="small"
              margin="dense"
              disabled={isDrawingMode}
              helperText="Sprinkler coverage radius in feet"
            />
          </Grid>
        )}
      </Grid>
      
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button
          onClick={onCancel}
          disabled={isDrawingMode}
          style={{ marginRight: 8 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          disabled={isDrawingMode}
        >
          Save Changes
        </Button>
      </Box>
    </Paper>
  );
};

AssetEditor.propTypes = {
  editingAsset: PropTypes.object,
  onEditChange: PropTypes.func.isRequired,
  onStyleChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isDrawingMode: PropTypes.bool.isRequired
};

// Map Provider Controls Component
export const MapProviderControls = ({
  providers,
  currentProvider,
  onProviderChange,
  isDrawingMode
}) => {
  const classes = useStyles();
  
  return (
    <Box mb={2}>
      <Paper elevation={1} style={{ padding: 8 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="body2">Map Provider:</Typography>
          </Grid>
          <Grid item>
            <ButtonGroup size="small">
              {providers.map(provider => (
                <Button
                  key={provider.name}
                  onClick={() => onProviderChange(provider)}
                  variant={currentProvider.name === provider.name ? 'contained' : 'outlined'}
                  color={currentProvider.name === provider.name ? 'primary' : 'default'}
                  disabled={isDrawingMode}
                >
                  {provider.name}
                </Button>
              ))}
            </ButtonGroup>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

MapProviderControls.propTypes = {
  providers: PropTypes.array.isRequired,
  currentProvider: PropTypes.object.isRequired,
  onProviderChange: PropTypes.func.isRequired,
  isDrawingMode: PropTypes.bool.isRequired
};

// Export all components
export default {
  DrawingToolbar,
  AssetList,
  AssetEditor,
  MapProviderControls
};
